/*
 *@providesModule InterstitialsManager
 *
 */

import {ImageSourcePropType, NativeModules, Platform} from 'react-native';

import {IInterstitialBannerProps, IInterstitialProps} from '@skillz/ui';
import AssetHelper from 'AssetHelper';
import {LTO_BANNERS_RECEIVED, LTO_INTERSTITIALS_TRIGGERED} from 'AWSConstants';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {
  INTERSTITIALS_PREFERENCE_TIMESTAMP,
  Interstitials as InterstitialsEvents,
} from 'InterstitialConstants';
import InterstitialModal from 'InterstitialModal';
import {get, isNumber} from 'lodash';
import ModalNavigationController from 'ModalNavigationController';
import NetClient from 'NetClient';
import {ErrorResponse} from 'NetClientTypes';
import Preferences from 'Preferences';
import SyncUtils from 'SyncUtils';
import React from 'react';
import {_m} from 'INTL';
import {InterstitialProps} from 'InterstitialModalUtils';

const SKZAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;

export const INTERSTITIAL_POP_UP_DELAY = 1500;

export interface IBannerProps extends IInterstitialBannerProps {
  data: InterstitialProps;
  interstitial_type: InterstitialProps['interstitial_type'];
  user_interstitial_id?: InterstitialProps['user_interstitial_id'];
}

// TODO fix the interstitial component for empty arrays and REMOVE the following constants
const GRADIENT_COLORS = {
  CLAIM: ['#00BD51', '#056241'],
  PLUS: ['#EB006B', '#A101A0'],
  TIMER: ['#0124A1', '#AD00FF'],
  BONUS: ['#005424', '#02DA60'],
};

// ============================================================================
// DAILY BONUS LTO DAY IMAGE MAPPING
// ============================================================================
// Maps day numbers (1-indexed) to day overlay images for Daily Bonus LTO banners
// Images are stored in sdk-mobile/source_images/daily_bonus_lto_days/
// ============================================================================
const getDailyBonusLTODayImage = (day: number): ImageSourcePropType | undefined => {
  // Format day number with leading zero (01, 02, ..., 30)
  const dayStr = day.toString().padStart(2, '0');
  const assetName = `daily_bonus_lto_days/DailyBonusDay${dayStr}.png`;
  
  try {
    const imageUri = AssetHelper.pathForAssetNamed(assetName, false);
    if (imageUri) {
      return { uri: imageUri };
    }
  } catch (error) {
    console.warn(`Failed to load Daily Bonus LTO day ${day} image:`, error);
  }
  return undefined;
};

const buildDailyBonusLTODayImagesMap = (): Map<number, ImageSourcePropType> | undefined => {
  const dayImagesMap = new Map<number, ImageSourcePropType>();
  
  // Build map for all days 1-30
  for (let day = 1; day <= 30; day++) {
    const dayImage = getDailyBonusLTODayImage(day);
    if (dayImage) {
      dayImagesMap.set(day, dayImage);
    }
  }
  
  return dayImagesMap.size > 0 ? dayImagesMap : undefined;
};

/**
 * Maps API response fields (snake_case) to InterstitialProps (camelCase)
 * Specifically maps:
 * - deferred_bonus_cash -> deferredBonus
 * - daily_reward_duration_days -> dailyRewardDurationDays
 */
const mapApiResponseToInterstitialProps = (
  apiResponse: any
): InterstitialProps => {
  const mapped: InterstitialProps = {
    ...apiResponse,
    // Map deferred_bonus_cash to deferredBonus (if present in API response)
    deferredBonus: apiResponse.deferred_bonus_cash !== null && apiResponse.deferred_bonus_cash !== undefined
      ? apiResponse.deferred_bonus_cash
      : apiResponse.deferredBonus,
    // Map daily_reward_duration_days to dailyRewardDurationDays (if present in API response)
    dailyRewardDurationDays: apiResponse.daily_reward_duration_days !== null && apiResponse.daily_reward_duration_days !== undefined
      ? apiResponse.daily_reward_duration_days
      : apiResponse.dailyRewardDurationDays,
    // Map highlight_strategy to highlightStartegy (note: keeping the typo in the codebase for consistency)
    highlightStartegy: apiResponse.highlight_strategy !== null && apiResponse.highlight_strategy !== undefined
      ? apiResponse.highlight_strategy
      : apiResponse.highlightStartegy,
  };
  return mapped;
};

export default class InterstitialsManager {
  //Lock for interstitials; prevents different triggers that trigger near each other from showing interstitials simultaneously
  private static canShowInterstitial = true;

  //Timestamp for interstitials lock as a failsafe
  private static canShowInterstitialTimestamp = new Date().getTime();

  //If true, the FTUE interstitial will be shown over any other interstitial post-match
  static ftueInterstitialCanBeSeen = false;

  // Cached banners - updated when fetchBannerInterstitials is called
  private static lowZBalanceBanners: IBannerProps[] = [];

  static recordToBanner = (record: InterstitialProps): IBannerProps => {
    let bannerAd: ImageSourcePropType | undefined = undefined;
    
    // Determine banner URL based on Daily Bonus LTO state and daily_reward_info
    let bannerUrl: string | null | undefined = record.banner_img_url;
    
    const isDailyBonusLTO = record.interstitial_type === 'DAILY_BONUS';
    const isClaimActive = (record.user_interstitial_state as any) === 'CLAIM_ACTIVE';
    const hasDailyRewardInfo = record.daily_reward_info !== null && record.daily_reward_info !== undefined;
    // Check if day is valid (greater than 0) - day 0 should not show daily claim banner
    const dailyRewardDay = record.daily_reward_info?.day;
    const isValidDay = dailyRewardDay !== undefined && dailyRewardDay > 0;
    
    // For Daily Bonus LTO with CLAIM_ACTIVE state and daily_reward_info, use daily_claim_banner_img_url
    // Only use daily_claim_banner_img_url if day is valid (greater than 0)
    if (isDailyBonusLTO && isClaimActive && hasDailyRewardInfo && isValidDay) {
      bannerUrl = record.daily_claim_banner_img_url;
    } else if (hasDailyRewardInfo && isValidDay) {
      // Fallback: if daily_reward_info is present (but not CLAIM_ACTIVE), still use daily_claim_banner_img_url
      // Only if day is valid (greater than 0)
      bannerUrl = record.daily_claim_banner_img_url;
    }
    
    // Default to banner_img_url if daily_claim_banner_img_url is not available
    if (!bannerUrl) {
      bannerUrl = record.banner_img_url;
    }
    
    if (bannerUrl) {
      bannerAd = {
        uri: AssetHelper.pathForAssetNamed(bannerUrl, false),
      };
    }
    
    // Map Daily Bonus LTO fields
    const dailyBonusLTOData = isDailyBonusLTO
      ? {
          // From triggered API (AVAILABLE state)
          deferredBonus: record.deferredBonus,
          days: record.dailyRewardDurationDays,
          // From active API (ONGOING state)
          dailyRewardMetadata: record.daily_reward_metadata
            ? {
                day: record.daily_reward_metadata.day,
                currency: record.daily_reward_metadata.currency,
                reward_amount:
                  typeof record.daily_reward_metadata.reward_amount === 'string'
                    ? parseFloat(record.daily_reward_metadata.reward_amount)
                    : record.daily_reward_metadata.reward_amount,
                claim_state: record.daily_reward_metadata.claim_state,
                claim_expiry_time: record.daily_reward_metadata.claim_expiry_time
                  ? new Date(record.daily_reward_metadata.claim_expiry_time)
                  : undefined,
                daily_banner_img_url: record.daily_reward_metadata.daily_banner_img_url,
                viewed: record.daily_reward_metadata.viewed,
              }
            : undefined,
          // Computed fields
          instant_bonus: record.bonus_cash,
          total_days: record.dailyRewardDurationDays,
        }
      : undefined;
    
    // Determine duration_end for Daily Bonus LTO
    // Handle null values from API - convert to undefined
    let durationEnd: Date | number | undefined = 
      record.duration_end === null ? undefined : record.duration_end;
    
    // For CLAIM_ACTIVE state with daily_reward_info, use claim_expiry_time from daily_reward_info
    if (
      isDailyBonusLTO &&
      (record.user_interstitial_state as any) === 'CLAIM_ACTIVE' &&
      hasDailyRewardInfo &&
      record.daily_reward_info?.claim_expiry_time
    ) {
      // Convert ISO string to Date or timestamp
      durationEnd = new Date(record.daily_reward_info.claim_expiry_time).getTime();
    }
    // For ONGOING state with available claim_state, use claim_expiry_time from daily_reward_metadata
    else if (
      isDailyBonusLTO &&
      record.user_interstitial_state === 'ONGOING' &&
      record.daily_reward_metadata?.claim_state === 'AVAILABLE' &&
      record.daily_reward_metadata?.claim_expiry_time
    ) {
      // Convert ISO string to Date or timestamp
      durationEnd = new Date(record.daily_reward_metadata.claim_expiry_time).getTime();
    }
    
    // Build day images map for Daily Bonus LTO
    const dailyBonusLTODayImages = isDailyBonusLTO 
      ? buildDailyBonusLTODayImagesMap() 
      : undefined;
    
    // Get default day image (for now, same for all days)
    // const defaultDayImage = isDailyBonusLTO 
    //   ? getDailyBonusLTODayImage(1) 
    //   : undefined;
    
    const bannerProps = {
      id: record.id,
      banner_asset_image: bannerAd,
      duration_end: durationEnd,
      data: JSON.parse(JSON.stringify(record)),
      interstitial_type: record.interstitial_type,
      user_interstitial_id: record.user_interstitial_id,
      // Note: onPress will be overridden by InterstitialBannerSlider.renderBanner
      // This is just a placeholder to satisfy the interface requirement
      onPress: (_id: string) => {
        // This should never be called as InterstitialBannerSlider overrides onPress
        console.warn('InterstitialBanner onPress called but not overridden by InterstitialBannerSlider', _id);
      },
      // Daily Bonus LTO props
      isDailyBonusLTO,
      dailyBonusLTOStatus: record.user_interstitial_state,
      dailyBonusLTOData,
      highlightStrategy: record.highlightStartegy || 'NONE',
      dailyBonusLTODayImages,
      // defaultDayImage,
    } as IBannerProps;
    
    return bannerProps;
  };

  /**
   * Pauses interstitial trigger handling; any triggers sent while paused will
   * be ignored
   */
  static pause = () => {
    this.canShowInterstitial = false;
  };

  /**
   * Resumes interstitial trigger handling
   */
  static resume = () => {
    this.canShowInterstitial = true;
  };

  /**
   * Displays multiple interstitials in a sequence, one after the other
   *
   * onComplete - callback to trigger once the last interstitial has been dismissed
   */
  static showInterstitials = (records: InterstitialProps[], onComplete?: () => void) => {
    ModalNavigationController.showModals(
      records.map((record, index) => ({
        component: this.toInterstitialModal(
          record,
          // Pass the onComplete callback to the final interstitial
          index === records.length - 1 ? onComplete : undefined
        ),
        props: {deduplicationID: record.id},
      }))
    );
  };

  /**
   * Displays an interstitial with the given data
   *
   * onComplete - callback to trigger when the interstitial is dismissed
   */
  static showInterstitial = (record: InterstitialProps, onComplete?: () => void) => {
    ModalNavigationController.showModal(this.toInterstitialModal(record, onComplete), {
      deduplicationID: record.id,
    });
  };

  static async fetchBannerInterstitials(): Promise<IBannerProps[]> {
    if (!ClientPropertyConstants.getInterstitialsEnabled()) {
      return [];
    }

    const currentUserId = await CurrentUserStore.getCurrentUserId();
    const game_id = get(SkillzModule, 'App.GameId');
    const url = `user_interstitial/active?game_id=${game_id}&user_id=${currentUserId}`;
    return new Promise(
      (resolve: (payload: IBannerProps[]) => void, reject: (error: Object) => void) => {
        // @ts-ignore - when the 'params' param is '{}' instead of 'null', this request throws a 400 cloud-front related error on Android
        NetClient.getURL(url, null, SKZAPIManager.OnError.promptRetry)
          .then((rawResponse: any[]) => {
            // Map API response fields (deferred_bonus_cash -> deferredBonus, etc.)
            const response = rawResponse.map(
              mapApiResponseToInterstitialProps
            ) as InterstitialProps[];

            // Filter out DAILY_BONUS interstitials on web platform
            const filteredResponse =
              Platform.OS === 'web'
                ? response.filter((record) => record.interstitial_type !== 'DAILY_BONUS')
                : response;

            const banners: IBannerProps[] = [];
            const assetSet = new Set();
            if (filteredResponse && filteredResponse.length) {
              filteredResponse
                .sort((a, b) => InterstitialsManager.sortBanners(a, b))
                .forEach((record) => {
                  const {banner_img_url} = record;
                  if (assetSet.has(banner_img_url)) return;
                  assetSet.add(banner_img_url);
                  banners.push(InterstitialsManager.recordToBanner(record));
                });
            }

            const ltoIds = banners.map(({id}) => id);
            AWSManager.trackEvent(LTO_BANNERS_RECEIVED, {
              category: 'LOT',
              data: JSON.stringify({ltoIds}),
            });
            // Update cached banners
            InterstitialsManager.lowZBalanceBanners = []
            const lowZBalanceBannerId = String(ClientPropertyConstants.getLowZBalanceLtoId())
            response.sort((a, b) => InterstitialsManager.sortBanners(a, b)).forEach((record) => {
              if(String(record.id) === lowZBalanceBannerId)
              InterstitialsManager.lowZBalanceBanners.push(InterstitialsManager.recordToBanner(record))
            });
            resolve(banners);
          })
          .catch((error: Object) => {
            console.debug('Error in fetchBannerInterstitials - InterstitialsManager', error);
            reject(error);
          });
      }
    );
  }

  /**
   * Gets the cached banners
   * @returns The cached banners array or null if not yet fetched
   */
  static getLowZBalanceBanners(): IBannerProps[] {
    return InterstitialsManager.lowZBalanceBanners;
  }

  /**
   * Clears the cached banners
   */
  static clearLowZBalanceBanners(): void {
    InterstitialsManager.lowZBalanceBanners = [];
  }

  /**
   * Helper function used to sort interstitials
   *
   * Currently, this is in order of increasing priority (lower priority should
   * come first) and decreasing deposit amount (higher deposit amounts should
   * come first)
   */
  private static sortBanners(
    {priority: aPriority, deposit_amount: aDepositAmount = 0}: InterstitialProps,
    {priority: bPriority, deposit_amount: bDepositAmount = 0}: InterstitialProps
  ) {
    const priorityComparison = aPriority - bPriority;
    if (priorityComparison !== 0) {
      return priorityComparison;
    }

    // We want the highest deposit amount first, so we reverse the
    // order of A and B in this difference
    return bDepositAmount - aDepositAmount;
  }

  static async nonSaleTrack(config_id: string) {
    const currentUserId = await CurrentUserStore.getCurrentUserId();
    const url = `user_interstitial/claim?interstitial_config_id=${config_id}&user_id=${currentUserId}`;
    NetClient.putURL(url, {}, 'true', (error: unknown | null, payload?: unknown) => {});
  }

  static async sendTrigger(
    triggerName: string,
    {
      onComplete,
      skipPopup = false,
      context = {},
    }: {onComplete?: () => void; skipPopup?: boolean; context?: any} = {},
    {isFtueScreen = false}: {isFtueScreen?: boolean} = {}
  ): Promise<InterstitialProps[] | undefined> {
    if (
      !ClientPropertyConstants.getInterstitialsEnabled() &&
      !ClientPropertyConstants.getInterstitialsHouseAdsEnabled()
    ) {
      onComplete?.();
      return;
    }

    //Sanity check for the canShowInterstitial lock
    //If interstitials have been locked for > 5 minutes, we assume we somehow entered a bad state and force an unlock
    const canShowInterstitialTimestampDiffInSeconds =
      (new Date().getTime() - this.canShowInterstitialTimestamp) / 1000;
    if (this.canShowInterstitial == false && canShowInterstitialTimestampDiffInSeconds > 300) {
      this.canShowInterstitial = true;
    }

    const gameId = SkillzModule.App.GameId;
    if (
      triggerName &&
      Object.values(InterstitialsEvents).includes(triggerName) &&
      gameId &&
      this.canShowInterstitial &&
      !SyncUtils.isMatchmakingActive()
    ) {
      try {
        this.canShowInterstitialTimestamp = new Date().getTime();
        this.canShowInterstitial = false;
        const currentUserId = await CurrentUserStore.getCurrentUserId();
        const url = `user_interstitial/triggered/${triggerName}?game_id=${gameId}&user_id=${currentUserId}`;

        const shouldBlockInterstitials = ClientPropertyConstants.getMigrateUserEnabled();
        if (shouldBlockInterstitials) {
          return undefined;
        }

        // Check the cooldown for the popup before making the API call
        const cooldown = ClientPropertyConstants.getInterstitialsGlobalCooldown();
        const lastShown = Preferences.get(INTERSTITIALS_PREFERENCE_TIMESTAMP);
        const timeDiffInSeconds = (new Date().getTime() - lastShown) / 1000;
        if (lastShown && timeDiffInSeconds < cooldown) {
          onComplete?.();
          return Promise.resolve([]);
        }

        return new Promise((resolve: (payload: InterstitialProps[]) => void) => {
          NetClient.postURL(
            url,
            context,
            'true',
            (error: ErrorResponse | null, payload?: Object) => {
              const rawResponse = payload as any[] | undefined;
              // Map API response fields (deferred_bonus_cash -> deferredBonus, etc.)
              const triggeredInterstitials = rawResponse?.map(mapApiResponseToInterstitialProps) as InterstitialProps[] | undefined;
              if (!error && triggeredInterstitials?.length) {
                // Filter out DAILY_BONUS interstitials on web platform
                const interstitialsAfterDailyBonusFilter =
                  Platform.OS === 'web'
                    ? triggeredInterstitials.filter(
                        (record) => record.interstitial_type !== 'DAILY_BONUS'
                      )
                    : triggeredInterstitials;

                /** If interstitials client prop is disabled, this means the user has the old interstitials system, therefore, we want to show only house-ads interstitials */
                const shouldShowOnlyHouseAds =
                  !ClientPropertyConstants.getInterstitialsEnabled() &&
                  ClientPropertyConstants.getInterstitialsHouseAdsEnabled();

                /** Filter out house ads if client prop is disabled  */
                const shouldFilterHouseAdsOut =
                  !ClientPropertyConstants.getInterstitialsHouseAdsEnabled();

                let filteredInterstitials: InterstitialProps[] = interstitialsAfterDailyBonusFilter;

                if (shouldShowOnlyHouseAds) {
                  filteredInterstitials = interstitialsAfterDailyBonusFilter.filter(
                    ({interstitial_type}) => interstitial_type === 'HOUSE_AD'
                  );
                }

                if (shouldFilterHouseAdsOut) {
                  filteredInterstitials = interstitialsAfterDailyBonusFilter.filter(
                    ({interstitial_type}) => interstitial_type !== 'HOUSE_AD'
                  );
                }

                const sortedInterstitials = filteredInterstitials.sort((a, b) =>
                  InterstitialsManager.sortBanners(a, b)
                );

                const ltoIds = sortedInterstitials.map(({id}) => id);
                AWSManager.trackEvent(LTO_INTERSTITIALS_TRIGGERED, {
                  category: 'LTO',
                  data: JSON.stringify({ltoIds}),
                });

                // Update the interstitial banners since we know we've triggered new
                // interstitials
                GlobalEventEmitter.emit('InterstitialBannerIconUpdate');

                if (skipPopup) {
                  onComplete?.();
                  return resolve(sortedInterstitials);
                }

                // Update the last shown timestamp
                Preferences.set({[INTERSTITIALS_PREFERENCE_TIMESTAMP]: new Date().getTime()});

              if (!isFtueScreen) {
                InterstitialsManager.showInterstitials(
                  InterstitialsManager.getDisplayedInterstitials(sortedInterstitials),
                  onComplete
                );
              } else {
                onComplete?.();
                return resolve(sortedInterstitials);
              }
              return resolve(sortedInterstitials);
            } else {
              onComplete?.();
              return resolve(triggeredInterstitials || []);
            }
          });
        });
      } catch {
        onComplete?.();
      } finally {
        this.canShowInterstitial = true;
      }
    } else {
      onComplete?.();
    }
    return;
  }

  /**
   * Returns the interstitials that should be shown out of the given
   * interstitials; currently, this is just 1 interstitial (no associated offer)
   * and one offer
   * we then sort by priority
   */
  private static getDisplayedInterstitials(
    interstitials: InterstitialProps[]
  ): InterstitialProps[] {
    const highestPriorityInterstitial = interstitials.find(
      ({deposit_amount}) => !isNumber(deposit_amount)
    );
    const highestPriorityOffer = interstitials.find(({deposit_amount}) => isNumber(deposit_amount));
    const displayedInterstitials = [highestPriorityInterstitial, highestPriorityOffer].filter(
      (interstitial) => interstitial
    ) as InterstitialProps[];
    return displayedInterstitials.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Returns the InterstitialModal component that will render the data for the
   * given interstitial
   */
  private static toInterstitialModal(interstitial: InterstitialProps, onComplete?: () => void) {
    return (
      <InterstitialModal
        data={{
          ...interstitial, // TODO fix the interstitial component for empty arrays

          flavour_copy_text:
            interstitial.flavour_copy_text?.trim() ||
            (isNumber(interstitial.redemptions_allowed)
              ? _m('{0} Available', interstitial.redemptions_allowed)
              : ''),
          claim_gradients:
            interstitial.claim_gradients?.length === 0
              ? GRADIENT_COLORS.CLAIM
              : interstitial.claim_gradients,
          bonus_gradients:
            interstitial.bonus_gradients?.length === 0
              ? GRADIENT_COLORS.BONUS
              : interstitial.bonus_gradients,
          timer_gradients:
            interstitial.timer_gradients?.length === 0
              ? GRADIENT_COLORS.TIMER
              : interstitial.timer_gradients,
          plus_gradients:
            interstitial.plus_gradients?.length === 0
              ? GRADIENT_COLORS.PLUS
              : interstitial.plus_gradients,
        }}
        onDismiss={() => {
          // Track the close of non-offer interstitials
          const {id, deposit_amount = 0} = interstitial;
          if (!deposit_amount) {
            InterstitialsManager.nonSaleTrack(id);
          }
          onComplete?.();
        }}
      />
    );
  }

  /**
   * Returns the game specific banner URL to show behind the interstitial banners
   *
   * This is currently hard-coded to a specific S3 location:
   * https://skillz.slack.com/archives/C06A5S8TWMT/p1721772403042649
   *
   * TODO: Refactor this to read from theme instead
   * https://skillzinc.atlassian.net/browse/RP-1289
   */
  static get bannerUrl(): string {
    return `${this.bannerUrlPrefix}/interstitial/background-image/${SkillzModule.App.GameId}-BackgroundImage.png`;
  }

  private static get bannerUrlPrefix(): string {
    switch (SKZAPIManager.CurrentEnvironment) {
      case SKZAPIManager.Environment.Prod:
        return 'https://media-asset.cdn.skillz.com';
      case SKZAPIManager.Environment.Staging:
        return 'https://media-asset.cdn.staging.skillz.com';
      case SKZAPIManager.Environment.QA:
      default:
        return 'https://skillz-media-asset-dev.s3.us-west-2.amazonaws.com';
    }
  }
}
